import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { SWRConfiguration } from 'swr';
import { DEDUP_INTERVAL_AUTOCOMPLETE_SUGGESTION } from '../../constants/swr-config';
import { SimpleUserForElastic } from '../../model/types';
import { fetcherWithToken } from '../../utils/fetchers';
import { getMaskedEmailForUI, nonNullishValue } from '../../utils/helpers';
import useSWRCacheKeys from '../useSWRCacheKeys';
import { AutocompleteResponseType } from './useAutocompleteSearch';
import { AutoCompleteUserKeyMap, generateUserFriendlyValueFromSimpleUser } from './useAutocompleteUserSearch';

export default function useAutocompleteUserSuggestions(
    autoCompleteUserKey: AutoCompleteUserKeyMap,
    needSuggestions: boolean,
    config: SWRConfiguration = {}
) {
    const keys = useSWRCacheKeys(`/field-values/${autoCompleteUserKey}/options`);
    const { t } = useTranslation('common');
    const [isSuggestionRequested, setIsSuggestionRequested] = useState(false);
    const isAbleToMakeRequest = nonNullishValue(keys) && needSuggestions;
    const { data, error, isLoading, mutate } = useSWR<AutocompleteResponseType<SimpleUserForElastic>>(
        isAbleToMakeRequest && isSuggestionRequested ? keys : null,
        fetcherWithToken,
        { revalidateIfStale: true, dedupingInterval: DEDUP_INTERVAL_AUTOCOMPLETE_SUGGESTION, ...config }
    );

    const searchResultAsOptions = useMemo(
        () =>
            data?.result?.map((user) => {
                const generatedLabel = generateUserFriendlyValueFromSimpleUser(user);
                return {
                    label: generatedLabel,
                    value: `${user.id}|${generatedLabel}`,
                    disabled: !getMaskedEmailForUI(user.email)
                };
            }),
        [data]
    );
    const translatedError = useMemo(() => {
        if (error) {
            if (error?.response?.data?.message?.includes('Search term must be at least'))
                return { message: t('searchTermTooShort') } as Error;
            else return error;
        } else return undefined;
    }, [error, t]);
    return {
        data: searchResultAsOptions,
        error: translatedError,
        isLoading,
        mutate,
        setIsSuggestionRequested
    };
}
